import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingZone } from '../../../../core/models/parking/parking-zone.model';
import { ApiParkingZoneService } from '../../../../core/services/api/parking/api-parking-zone.service';
import { ModalParkingZoneComponent } from '../../modals/parking/parking-zone/modal-parking-zone/modal-parking-zone.component';

@Component({
  selector: 'app-parking-zone',
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingZoneComponent],
  templateUrl: './parking-zone.component.html'
})
export class ParkingZoneComponent implements OnInit {
  public list: ParkingZone[] = [];
  public filteredList: ParkingZone[] = [];
  public isLoading: boolean = false;

  public searchText: string = '';
  public filterFloor: string = '';
  public filterParkingType: string = '';

  public isFloorDropdownOpen: boolean = false;
  public isParkingTypeDropdownOpen: boolean = false;

  public floorOptions: string[] = [];
  public parkingTypeOptions: string[] = [];

  public selectedIds: Set<number> = new Set<number>();

  public sortColumn: string = 'id';
  public sortDirection: 'asc' | 'desc' = 'asc';

  public isModalOpen: boolean = false;
  public selectedItem: ParkingZone | null = null;

  public dashboardStats: any[] = [];

  constructor(
    private apiService: ApiParkingZoneService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.fetchData();
  }

  fetchData(): void {
    this.isLoading = true;
    this.apiService.getParkingZones().subscribe({
      next: (res) => {
        this.list = res || [];
        this.updateFilterOptions();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
        this.isLoading = false;
      }
    });
  }

  updateFilterOptions(): void {
    this.floorOptions = Array.from(new Set(this.list.map(i => i.floor))).filter(Boolean).sort();
    this.parkingTypeOptions = Array.from(new Set(this.list.map(i => i.parkingType))).filter(Boolean).sort();
  }

  calculateStats(): void {
    const total = this.list.length;
    const isOnlineTotal = this.list.filter(p => p.isOnline).length;
    const totalLots = this.list.reduce((acc, curr) => acc + curr.lot, 0);

    this.dashboardStats = [
      { title: 'โซนทั้งหมด', value: total.toLocaleString(), icon: 'grid_view', color: 'text-indigo-600 bg-indigo-50' },
      { title: 'ออนไลน์', value: isOnlineTotal.toLocaleString(), icon: 'cloud_done', color: 'text-emerald-500 bg-emerald-50' },
      { title: 'รวม Lot ทั้งหมด', value: totalLots.toLocaleString(), icon: 'meeting_room', color: 'text-amber-500 bg-amber-50' }
    ];
  }

  applyFilters(): void {
    let result = [...this.list];

    if (this.searchText) {
      const s = this.searchText.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(s) ||
        item.floor.toLowerCase().includes(s) ||
        item.parkingType.toLowerCase().includes(s)
      );
    }

    if (this.filterFloor) {
      result = result.filter(item => item.floor === this.filterFloor);
    }

    if (this.filterParkingType) {
      result = result.filter(item => item.parkingType === this.filterParkingType);
    }

    // Sort
    if (this.sortColumn) {
      result.sort((a: any, b: any) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];

        if (typeof valA === 'string') {
          return this.sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    this.filteredList = result;
  }

  // Action / UI
  toggleFloorDropdown(): void {
    this.isFloorDropdownOpen = !this.isFloorDropdownOpen;
    this.isParkingTypeDropdownOpen = false;
  }

  toggleParkingTypeDropdown(): void {
    this.isParkingTypeDropdownOpen = !this.isParkingTypeDropdownOpen;
    this.isFloorDropdownOpen = false;
  }

  selectFloor(val: string): void {
    this.filterFloor = val;
    this.isFloorDropdownOpen = false;
    this.applyFilters();
  }

  selectParkingType(val: string): void {
    this.filterParkingType = val;
    this.isParkingTypeDropdownOpen = false;
    this.applyFilters();
  }

  onSearch(): void {
    this.applyFilters();
  }

  onRefresh(): void {
    this.searchText = '';
    this.filterFloor = '';
    this.filterParkingType = '';
    this.selectedIds.clear();
    this.fetchData();
  }

  sortData(col: string): void {
    if (this.sortColumn === col) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = col;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  navigateToCreate(): void {
    this.selectedItem = null;
    this.isModalOpen = true;
  }

  openEditModal(item: ParkingZone): void {
    this.selectedItem = item;
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedItem = null;
  }

  // Selection
  toggleSelection(id: number, event?: Event): void {
    if (event) event.stopPropagation();
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  toggleAllSelection(): void {
    if (this.isAllSelected() && this.filteredList.length > 0) {
      this.selectedIds.clear();
    } else {
      this.filteredList.forEach(p => this.selectedIds.add(p.id));
    }
  }

  isAllSelected(): boolean {
    return this.filteredList.length > 0 && this.selectedIds.size === this.filteredList.length;
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  // Toggle Live Pill
  toggleStatusLive(item: ParkingZone): void {
    item.isOnline = !item.isOnline;
    setTimeout(() => {
      this.toast.success(`เปลี่ยนสถานะเป็น ${item.isOnline ? 'Online' : 'Offline'} สำเร็จ`);
      this.calculateStats();
    }, 300);
  }
}
